// Critical path analytics events per docs/critical-falloff-analytics-guide.md
// These events define the mandatory user journey:
// app_opened → signup_started → signup_completed → onboarding_started →
// onboarding_completed → core_action_started → core_action_completed →
// returned_day_1 → returned_day_7

export type Severity = 'low' | 'medium' | 'high' | 'fatal';

export const criticalPathEvents = {
  // ─── Entry & Commitment ────────────────────────────────────────

  appOpened: (properties: {
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    landing_page?: string;
  }) => ({
    event: 'app_opened',
    properties,
  }),

  heroCtaClicked: (ctaType: 'primary_signup' | 'secondary_how_it_works', variant?: string) => ({
    event: 'hero_cta_clicked',
    properties: {
      cta_type: ctaType,
      variant,
    },
  }),

  stickyCtaClicked: (device: 'mobile' | 'desktop') => ({
    event: 'sticky_cta_clicked',
    properties: { device },
  }),

  stickyCtaDismissed: () => ({
    event: 'sticky_cta_dismissed',
    properties: {},
  }),

  stickyCtaShown: () => ({
    event: 'sticky_cta_shown',
    properties: {},
  }),

  // ─── Email Confirmation ────────────────────────────────────────

  emailConfirmationSent: (method: 'signup' | 'resend') => ({
    event: 'email_confirmation_sent',
    properties: { method },
  }),

  emailConfirmed: () => ({
    event: 'email_confirmed',
    properties: {},
  }),

  emailConfirmationResend: () => ({
    event: 'email_confirmation_resend',
    properties: {},
  }),

  emailConfirmationFailed: (error: string) => ({
    event: 'email_confirmation_failed',
    properties: { error_message: error },
  }),

  // ─── Onboarding ───────────────────────────────────────────────

  onboardingStarted: () => ({
    event: 'onboarding_started',
    properties: {},
  }),

  onboardingCompleted: (properties: {
    total_time_ms: number;
    complexity_level?: string;
    situation_type?: string;
    state?: string;
  }) => ({
    event: 'onboarding_completed',
    properties,
  }),

  onboardingAbandoned: (properties: {
    last_step: string;
    step_number: number;
    total_time_ms: number;
  }) => ({
    event: 'onboarding_abandoned',
    properties,
  }),

  disclaimerRejected: () => ({
    event: 'disclaimer_rejected',
    properties: {},
  }),

  // ─── Core Action: AI Strategy Generation ───────────────────────

  coreActionStarted: (properties: {
    action_type: 'strategy_generation';
    is_regeneration: boolean;
    has_notes: boolean;
  }) => ({
    event: 'core_action_started',
    properties,
  }),

  coreActionCompleted: (properties: {
    action_type: 'strategy_generation';
    strategy_id: string;
    latency_ms: number;
    tokens_used?: number;
  }) => ({
    event: 'core_action_completed',
    properties,
  }),

  coreActionFailed: (properties: {
    action_type: 'strategy_generation';
    failure_reason: string;
    latency_ms: number;
    severity: Severity;
  }) => ({
    event: 'core_action_failed',
    properties,
  }),

  // ─── Retention ─────────────────────────────────────────────────

  returnedDay1: () => ({
    event: 'returned_day_1',
    properties: {},
  }),

  returnedDay7: () => ({
    event: 'returned_day_7',
    properties: {},
  }),

  returnedDay30: () => ({
    event: 'returned_day_30',
    properties: {},
  }),

  // ─── Friction & Failure ────────────────────────────────────────

  emptyStateShown: (screen: string, feature: string) => ({
    event: 'empty_state_shown',
    properties: {
      screen,
      feature,
    },
  }),

  formValidationError: (properties: {
    screen: string;
    field: string;
    error_code: string;
    recoverable: boolean;
    severity: Severity;
  }) => ({
    event: 'form_validation_error',
    properties,
  }),

  permissionDenied: (screen: string, reason: string) => ({
    event: 'permission_denied',
    properties: { screen, reason },
  }),

  // ─── Conversion Dead-Letter Events ──────────────────────────────

  strategyPreviewViewed: (properties: {
    strategy_id: string;
    scenario_count: number;
    marital_assets: number;
    tier: string;
  }) => ({
    event: 'strategy_preview_viewed',
    properties,
  }),

  dashboardFirstViewed: () => ({
    event: 'dashboard_first_viewed',
    properties: {},
  }),

  paywallDismissed: (properties: {
    feature: string;
    tier: string;
    time_visible_ms: number;
  }) => ({
    event: 'paywall_dismissed_dead_letter',
    properties,
  }),

  pricingPageExited: (properties: {
    tier: string;
    time_on_page_ms: number;
    ctas_clicked: number;
  }) => ({
    event: 'pricing_page_exited',
    properties,
  }),
};

export type CriticalPathEvent = ReturnType<typeof criticalPathEvents[keyof typeof criticalPathEvents]>;
